import {
  IconButton,
  Components as JoyComponents,
  Theme as JoyTheme,
} from '@mui/joy';
import JoyInput, {
  inputClasses,
  InputProps as JoyInputProps,
} from '@mui/joy/Input';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { mergeProps, mergeRefs, useObjectRef } from '@react-aria/utils';
import clsx from 'clsx';
import { forwardRef, useEffect, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import { Description, Label } from './fieldset';
import {
  ComposedFormControllerProps,
  FormController,
  useFormController,
} from './form';
import { createSplitProps } from './utils';

export const overrides: JoyComponents<JoyTheme>['JoyInput'] = {
  defaultProps: { variant: 'outlined' },
  styleOverrides: {
    root: ({ ownerState, theme }) => ({
      // Explicitly set the icon size for the button
      [`& .MuiSvgIcon-root, & .${inputClasses.startDecorator} > svg, & .${inputClasses.endDecorator} > svg`]:
        {
          margin: 'var(--Icon-margin)',
          fontSize: 'var(--Icon-fontSize, var(--joy-fontSize-xl2))',
          color:
            'var(--Icon-color, var(--joy-palette-text-icon, var(--joy-palette-neutral-500, #636B74)))',
        },
      ':hover': {
        backgroundColor:
          'var(--variant-outlinedHoverBg, var(--joy-palette-neutral-outlinedHoverBg, var(--joy-palette-neutral-50)))',
      },
      ...(ownerState.size === 'lg' && {
        '--Input-minHeight': theme.spacing(6),
        fontSize: theme.fontSize.md,
        lineHeight: theme.lineHeight.lg,
      }),
    }),
  },
};

export type InputProps = JoyInputProps;
export const Input = forwardRef<
  HTMLDivElement,
  InputProps & { controlled?: boolean }
>(function Input({ controlled = true, size, ...props }, ref) {
  const controller = useFormController();
  const objectRef = useObjectRef(ref);
  const mergedRef =
    controlled && controller
      ? mergeRefs(ref, objectRef, controller.field.ref)
      : mergeRefs(ref, objectRef);

  return (
    <JoyInput
      {...mergeProps(
        props,
        { size, 'data-slot': 'control', ref: mergedRef },
        controller && controlled ? { ...controller?.field } : {},
      )}
    />
  );
});

export const PasswordInput = forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof Input>
>(function PasswordInput(props, ref) {
  const intl = useIntl();
  const [show, setShow] = useState(false);
  const mergedProps = mergeProps(props, {
    endDecorator: (
      <IconButton
        onClick={() => setShow((prev) => !prev)}
        aria-label={
          show
            ? intl.formatMessage({ defaultMessage: 'Hide password' })
            : intl.formatMessage({ defaultMessage: 'Show password' })
        }
      >
        {show ? <Eye aria-hidden="true" /> : <EyeSlash aria-hidden="true" />}
      </IconButton>
    ),
    type: show ? 'text' : 'password',
  });
  return <Input {...mergedProps} autoComplete='off' ref={ref} />;
});

const fieldClasses = [
  '[&>[data-slot=label]+[data-slot=control]]:mt-2',
  '[&>[data-slot=label]+[data-slot=description]]:mt-1',
  '[&>[data-slot=description]+[data-slot=control]]:mt-2',
  '[&>[data-slot=control]+[data-slot=description]]:mt-2',
  '[&>[data-slot=control]+[data-slot=error]]:mt-2',
  '[&>[data-slot=label]]:font-medium',
];
export { fieldClasses, fieldClasses as inputFieldClasses };

export function InputField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  ...props
}: ComposedFormControllerProps<T, TName> &
  Omit<InputProps, keyof ComposedFormControllerProps<T, TName>> & {
    label?: React.ReactNode;
    description?: React.ReactNode;
  }) {
  const [controllerProps, inputProps] = createSplitProps<
    ComposedFormControllerProps<T, TName>
  >()(props, [
    'color',
    'color',
    'defaultValue',
    'disabled',
    'field',
    'control',
    'size',
    'required',
  ]);

  return (
    <FormController {...controllerProps}>
      <div className={clsx(fieldClasses)}>
        {label && <Label required={props.required}>{label}</Label>}
        <Input {...inputProps} />
        {description && <Description>{description}</Description>}
      </div>
    </FormController>
  );
}

export const DebouncedInput = forwardRef<HTMLInputElement, InputProps>(
  function DebouncedInput(props, ref) {
    const [value, setValue] = useState('');
    const controller = useFormController();

    const inputHandler = useDebouncedCallback(
      (e: Parameters<NonNullable<InputProps['onChange']>>[0]) => {
        controller?.field.onChange(e.target.value);
      },
      500,
    );
    // updates the value when the controller value changes
    useEffect(() => {
      setValue(controller?.field.value ?? '');
    }, [controller?.field.value]);

    return (
      <Input
        ref={ref}
        defaultValue={controller?.field.value}
        {...mergeProps(props, controller ? { ...controller.field } : {})}
        // resets the value to undefined to since we do not want to set the value from the controller
        value={value}
        controlled={false}
        onChange={(e) => {
          setValue(e.target.value);
          inputHandler(e);
        }}
      />
    );
  },
);
